import apiClient from './apiClient';
import { Place, PlaceDetail, ResultsFilter, SearchStatistics, FeatureAccess, DiscoverSummary, BestSearch } from '../types/results.types';

export interface AccessInfo {
  total_places_found: number;
  places_unlocked: number;
  places_locked: number;
  credits_charged: number;
  has_locked_results: boolean;
  all_locked: boolean;
  results_capped: boolean;
  total_links_found: number;
  visible_results_cap: number | null;
}

export const getResults = async (
  searchId: number,
  page: number = 1,
  limit: number = 20,
  filters?: ResultsFilter
): Promise<{
  results: Place[];
  total: number;
  pages: number;
  verifiable_emails_count: number;
  enrichable_people_count: number;
  access_info?: AccessInfo;
  pagination_full?: any;
  feature_access?: FeatureAccess;
  current_plan?: string;
  summary?: import('../types/results.types').ResultsSummary;
  auto_find_status?: string | null;
  auto_find_mode?: string | null;
  auto_find_success_limit?: number;
  auto_find_success_count?: number;
  auto_find_chargeable_count?: number;
  auto_find_attempt_count?: number;
  auto_find_no_email_count?: number;
  auto_find_catch_all_count?: number;
  auto_find_guardrail_reason?: string | null;
  auto_find_business_target?: number;
  auto_find_business_attempt_count?: number;
  auto_find_max_people_per_business?: number;
  auto_find_monthly_pool_limit?: number;
  auto_find_monthly_pool_used?: number;
}> => {
  const params = new URLSearchParams({
    page: page.toString(),
    items_per_page: limit.toString(),
    // Add other static params if needed
  });

  // Dynamically add filter parameters if they exist and are valid
  if (filters) {
    if (filters.industry) params.set('industries', filters.industry);
    if (filters.minRating) params.set('min_rating', filters.minRating.toString());
    if (filters.maxRating) params.set('max_rating', filters.maxRating.toString());
    if (filters.minReviews) params.set('min_reviews', filters.minReviews.toString());
    if (filters.maxReviews) params.set('max_reviews', filters.maxReviews.toString());
    if (filters.hasWebsite !== null && typeof filters.hasWebsite !== 'undefined') {
      params.set('has_website', String(filters.hasWebsite));
    }
    if (filters.hasPhone !== null && typeof filters.hasPhone !== 'undefined') {
      params.set('has_phone', String(filters.hasPhone));
    }
    if (filters.hasBookVisit !== null && typeof filters.hasBookVisit !== 'undefined') {
      params.set('has_book_visit', String(filters.hasBookVisit));
    }
    if (filters.hasDecisionMakers !== null && typeof filters.hasDecisionMakers !== 'undefined') {
      params.set('has_decision_makers', String(filters.hasDecisionMakers));
    }
    if (filters.hasEmail !== null && typeof filters.hasEmail !== 'undefined') {
      params.set('has_email', String(filters.hasEmail));
    }
    if (filters.emailVerificationStatus && filters.emailVerificationStatus.length > 0) {
      params.set('email_verification_status', filters.emailVerificationStatus.join(','));
    }
    if (filters.searchTerm) params.set('name', filters.searchTerm);
    if (filters.strongMatchesOnly) params.set('delivery_group', 'website_confirmed');
    if (filters.matchedOffering) params.set('matched_offering', filters.matchedOffering);
    if (filters.googleAdsSignal) params.set('google_ads_signal', filters.googleAdsSignal);
  }

  const response = await apiClient.get(`/searches/${searchId}/results?${params.toString()}`);
  
  // Map the API response to the format expected by the frontend
  const totalPlacesFound = response.data.pagination.total_places_found || 0;
  const placesLocked = response.data.pagination.places_locked || 0;
  const placesUnlocked = totalPlacesFound - placesLocked;
  
  return {
    results: response.data.items,
    total: response.data.pagination.total_items,
    pages: response.data.pagination.total_pages,
    verifiable_emails_count: response.data.verifiable_emails_count || 0,
    enrichable_people_count: response.data.enrichable_people_count || 0,
    access_info: {
      total_places_found: totalPlacesFound,
      places_unlocked: placesUnlocked,
      places_locked: placesLocked,
      credits_charged: response.data.pagination.credits_charged || 0,
      has_locked_results: response.data.pagination.has_locked_results || false,
      all_locked: response.data.pagination.all_locked || false,
      results_capped: response.data.pagination.results_capped || false,
      total_links_found: response.data.pagination.total_links_found || 0,
      visible_results_cap: response.data.pagination.visible_results_cap ?? null,
    },
    pagination_full: response.data.pagination,
    feature_access: response.data.feature_access || null,
    current_plan: response.data.current_plan || null,
    summary: response.data.summary || null,
    auto_find_status: response.data.auto_find_status || null,
    auto_find_mode: response.data.auto_find_mode || null,
    auto_find_success_limit: response.data.auto_find_success_limit || 0,
    auto_find_success_count: response.data.auto_find_success_count || 0,
    auto_find_chargeable_count: response.data.auto_find_chargeable_count || 0,
    auto_find_attempt_count: response.data.auto_find_attempt_count || 0,
    auto_find_no_email_count: response.data.auto_find_no_email_count || 0,
    auto_find_catch_all_count: response.data.auto_find_catch_all_count || 0,
    auto_find_guardrail_reason: response.data.auto_find_guardrail_reason || null,
    auto_find_business_target: response.data.auto_find_business_target || 0,
    auto_find_business_attempt_count: response.data.auto_find_business_attempt_count || 0,
    auto_find_max_people_per_business: response.data.auto_find_max_people_per_business || 0,
    auto_find_monthly_pool_limit: response.data.auto_find_monthly_pool_limit || 0,
    auto_find_monthly_pool_used: response.data.auto_find_monthly_pool_used || 0,
  };
};

function buildExportOrEstimateParams(filters?: ResultsFilter): URLSearchParams {
  const params = new URLSearchParams();
  if (filters) {
    if (filters.industry) params.set('industries', filters.industry);
    if (filters.minRating) params.set('min_rating', filters.minRating.toString());
    if (filters.maxRating) params.set('max_rating', filters.maxRating.toString());
    if (filters.minReviews) params.set('min_reviews', filters.minReviews.toString());
    if (filters.maxReviews) params.set('max_reviews', filters.maxReviews.toString());
    if (filters.hasWebsite !== null && typeof filters.hasWebsite !== 'undefined') {
      params.set('has_website', String(filters.hasWebsite));
    }
    if (filters.hasPhone !== null && typeof filters.hasPhone !== 'undefined') {
      params.set('has_phone', String(filters.hasPhone));
    }
    if (filters.hasBookVisit !== null && typeof filters.hasBookVisit !== 'undefined') {
      params.set('has_book_visit', String(filters.hasBookVisit));
    }
    if (filters.hasDecisionMakers !== null && typeof filters.hasDecisionMakers !== 'undefined') {
      params.set('has_decision_makers', String(filters.hasDecisionMakers));
    }
    if (filters.hasEmail !== null && typeof filters.hasEmail !== 'undefined') {
      params.set('has_email', String(filters.hasEmail));
    }
    if (filters.emailVerificationStatus && filters.emailVerificationStatus.length > 0) {
      params.set('email_verification_status', filters.emailVerificationStatus.join(','));
    }
    if (filters.searchTerm) params.set('name', filters.searchTerm);
    if (filters.strongMatchesOnly) params.set('delivery_group', 'website_confirmed');
    if (filters.matchedOffering) params.set('matched_offering', filters.matchedOffering);
    if (filters.googleAdsSignal) params.set('google_ads_signal', filters.googleAdsSignal);
  }
  return params;
}

export interface ExportEmailConfidenceEstimate {
  high_confidence: number;
  medium_confidence: number;
  low_confidence: number;
  unverified_count: number;
  verification_eligible_count: number;
  enrichable_people_count: number;
  ready_now_count: number;
  ready_personal_count: number;
  ready_company_count: number;
  expanded_export_count: number;
  filtered_lead_count: number;
  ready_decision_maker_email_count: number;
  ready_other_direct_email_count: number;
  shared_inbox_count: number;
  unverified_direct_email_count: number;
}

export type ExportCoverage = 'ready' | 'expanded';

export interface ExportSelection {
  coverage: ExportCoverage;
  includeSharedInboxes: boolean;
  includedEmailCount: number;
  filteredLeadCount: number;
  readyDecisionMakerEmailCount: number;
  readyOtherDirectEmailCount: number;
  sharedInboxCount: number;
  unverifiedDirectEmailCount: number;
}

export const estimateExportEmailConfidence = async (
  searchId: number,
  filters?: ResultsFilter
): Promise<ExportEmailConfidenceEstimate> => {
  const params = buildExportOrEstimateParams(filters);
  const response = await apiClient.get(
    `/searches/${searchId}/export/estimate?${params.toString()}`
  );
  return response.data;
};

export const exportResults = async (
  searchId: number,
  format: 'csv' | 'excel' = 'csv',
  filters?: ResultsFilter,
  coverage: ExportCoverage = 'ready',
  includeSharedInboxes: boolean = false
): Promise<Blob> => {
  const params = buildExportOrEstimateParams(filters);
  params.set('format', format);
  params.set('coverage', coverage);
  params.set('include_shared_inboxes', String(includeSharedInboxes));

  const response = await apiClient.get(`/searches/${searchId}/export?${params.toString()}`, {
    responseType: 'blob',
  });
  return response.data;
};

export const getPlaceDetail = async (searchId: number, placeId: number): Promise<PlaceDetail> => {
  const response = await apiClient.get(`/searches/${searchId}/places/${placeId}`);
  return response.data;
};

export const getStatistics = async (searchId: number): Promise<SearchStatistics> => {
  const response = await apiClient.get(`/searches/${searchId}/statistics`);
  return response.data;
};

export interface IndustryWithCount {
  id: number;
  name: string;
  count: number;
}

export const getIndustries = async (searchId: number): Promise<IndustryWithCount[]> => {
  const response = await apiClient.get(`/searches/${searchId}/industries`);
  return response.data.industries;
};

export interface EmailMetrics {
  emails_found: number;
  decision_makers_count: number;
  verified_count: number;
  verified_pct: number;
  likely_count: number;
  likely_pct: number;
  possible_count: number;
  possible_pct: number;
  headline: string;
}

export const getEmailMetrics = async (searchId: number): Promise<EmailMetrics> => {
  const response = await apiClient.get(`/searches/${searchId}/email-metrics`);
  return response.data;
};

export const getUserSummary = async (): Promise<DiscoverSummary> => {
  const response = await apiClient.get('/searches/user-summary');
  return response.data;
};

export const getBestSearch = async (): Promise<BestSearch | null> => {
  const response = await apiClient.get('/searches/best-completed');
  return response.data ?? null;
};
